import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useEffect, useRef } from 'react';
import type { CommunicationEntry } from '../../../../services/communications';
import { channelDef } from './channels';
import { useEscapeKey } from '../../../../hooks/useEscapeKey';

interface DeleteEntryConfirmModalProps {
    entry: CommunicationEntry;
    isDeleting: boolean;
    /** Set when the server rejected the delete (400 - last remaining entry on the thread). */
    errorMessage: string | null;
    onConfirm: () => void;
    onCancel: () => void;
}

// Sibling to DeleteCommunicationConfirmModal - same overlay/card/focus/Escape
// conventions, plus an inline error slot for the one rejection the server
// enforces: a thread's last remaining entry can't be deleted.
export function DeleteEntryConfirmModal({
    entry,
    isDeleting,
    errorMessage,
    onConfirm,
    onCancel
}: DeleteEntryConfirmModalProps) {
    const cancelRef = useRef<HTMLButtonElement>(null);
    const label = channelDef(entry.channel).label;

    useEffect(() => {
        cancelRef.current?.focus();
    }, []);

    useEscapeKey(onCancel, !isDeleting);

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-entry-modal-title"
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm"
        >
            <div className="bg-white rounded-xl shadow-lg w-full max-w-md p-6 relative">
                <button
                    onClick={onCancel}
                    disabled={isDeleting}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
                    aria-label="Close"
                >
                    <XMarkIcon className="w-5 h-5" />
                </button>

                <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                        <ExclamationTriangleIcon className="w-5 h-5 text-red-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3
                            id="delete-entry-modal-title"
                            className="text-lg font-semibold text-gray-900"
                        >
                            Delete this {label.toLowerCase()} interaction?
                        </h3>
                        <p className="text-sm text-gray-500 mt-1">
                            This action cannot be undone. This interaction will be removed from the
                            timeline.
                        </p>
                        {errorMessage && (
                            <p className="text-sm text-red-600 font-medium mt-3">{errorMessage}</p>
                        )}
                    </div>
                </div>

                <div className="flex justify-end items-center gap-3 mt-6">
                    <button
                        ref={cancelRef}
                        onClick={onCancel}
                        disabled={isDeleting}
                        className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={isDeleting}
                        className="px-4 py-2 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 inline-flex items-center gap-2"
                    >
                        {isDeleting ? (
                            <>
                                <div className="w-3.5 h-3.5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                                Deleting…
                            </>
                        ) : (
                            'Delete'
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}
